import React from 'react';
import { Link } from 'react-router-dom';
import { percentageLabel } from '../../lib/evaluationIntegrity';
import { personName, STAGES, SERVICE_TYPES, FINDING_STATUS, INCIDENT_STATUS, PROVIDER_STATUS } from '../../lib/traceability';
import StatusBadge from '../StatusBadge';
import SemaforoBadge from '../SemaforoBadge';
import { EvidenceList } from './FindingCard';
import EventPreparation from './EventPreparation';
import Records from './Records';

function Entries({ title, items = [], providers = false }) {
  return <section className="soem-card p-4"><h3 className="font-bold">{title}</h3>
    {!items.length && <p>Sin registros.</p>}
    <ul className="space-y-2">{items.map((item, index) => <li key={item.id || index}>
      {providers ? <>
        {/* #Fase3-G5 — El resumen usa campos públicos; responsable sí, datos privados del catálogo no. */}
        <p>{item.provider?.trade_name || item.trade_name}</p>
        <p>{SERVICE_TYPES[item.provider?.service_type || item.service_type]} · {item.service_date} · {PROVIDER_STATUS[item.status]}</p>
        <p>{item.provider?.contact_name || item.contact_name}</p>
      </> : typeof item === 'string' ? item : <>
        <p>{item.title || item.trade_name || item.provider?.trade_name || item.description || item.recommendation}</p>
        <p>{item.text || item.notes || item.observations}</p>
        <p>{{ bajo: 'Bajo', medio: 'Medio', alto: 'Alto', critico: 'Crítico', ...FINDING_STATUS, ...INCIDENT_STATUS, ...PROVIDER_STATUS }[item.level || item.status] || ''} {item.service_date} {item.contact_name || item.provider?.contact_name}</p>
        <p>{item.by && personName(item, 'by')} {item.at}</p>
        {item.evidence && <EvidenceList items={item.evidence} />}
      </>}
    </li>)}</ul>
  </section>;
}

export default function Dossier({ dossier, stage, event, user, canEdit, onChanged }) {
  const data = dossier?.[stage];
  const clientView = user?.role === 'cliente';
  if (!data) return <p role="status">Expediente no disponible.</p>;
  return <div className="space-y-5">
    <h2 className="text-xl font-bold">{STAGES[stage]} · Etapa actual: {STAGES[dossier.stage]}</h2>
    {stage === 'antes' && <>
      {clientView ? <section className="soem-card p-4">
        <h3 className="font-bold">Preparación del evento</h3>
        <p>Organizador: {event.organizer_name || 'Sin registrar'}</p>
        <p>Empresa organizadora: {event.organizer_company || 'Sin registrar'}</p>
      </section> : <EventPreparation event={event} canEdit={canEdit} onChanged={onChanged} />}
      <Entries title="Proveedores" items={data.providers} providers />
      <Entries title="Riesgos" items={data.risks} />
      <Entries title="Requerimientos" items={data.requirements} />
    </>}
    <section className="soem-card p-4 space-y-3"><h3 className="font-bold">Evaluaciones</h3>
      {!(data.evaluations || []).length && <p>Sin evaluaciones en esta etapa.</p>}
      {(data.evaluations || []).map((evaluation) => <div key={evaluation.id}>
        <Link className="text-blue-700 underline" to={user?.role === 'cliente' ? `/cliente/evaluaciones/${evaluation.id}` : `/dashboard/evaluaciones/${evaluation.id}/revision`}>
          {evaluation.name || 'Evaluación'}
        </Link>
        <StatusBadge status={evaluation.display_status || evaluation.status} />
        <SemaforoBadge percentage={evaluation.percentage} />
      </div>)}
    </section>
    {stage === 'durante' && <>
      <section className="soem-card p-4"><h3 className="font-bold">Personal</h3>
        <p>Asignados: {data.staff?.assigned ?? '—'} · Presentes: {data.staff?.present ?? '—'}</p>
        {(data.staff?.by_zone || []).map((zone, i) => <p key={zone.id || i}>
          {typeof zone.zone === 'string' ? zone.zone : zone.label || zone.zone?.label || 'Zona sin nombre'}
          : {zone.present ?? '—'} / {zone.assigned ?? '—'}
        </p>)}
      </section>
      <Entries title="Nuevos riesgos detectados durante la operación" items={data.nuevos_riesgos} />
      <Entries title="Incidencias" items={data.incidents} />
      <Entries title="Acciones durante el evento" items={data.actions} />
    </>}
    {stage === 'despues' && <>
      <section className="soem-card p-4"><h3 className="font-bold">Cierre de hallazgos</h3>
        <p>Total: {data.findings_summary?.total ?? '—'} · Mitigados: {data.findings_summary?.mitigado ?? '—'}</p>
        <p>No mitigados: {data.findings_summary?.no_mitigado ?? '—'} · Abiertos: {
          data.findings_summary?.abiertos ?? '—'
        }</p>
        {!clientView && <p>{dossier.audit_ready ? 'Listo para auditar' : 'Falta evaluación Después publicada o cerrar hallazgos'}</p>}
      </section>
      <section className="soem-card p-4"><h3 className="font-bold">Cumplimiento</h3>
        <p>Antes: {percentageLabel(data.compliance?.antes)} · Después: {percentageLabel(data.compliance?.despues)}</p>
        <p>Variación: {!Number.isFinite(data.compliance?.delta) ? 'Sin comparación' : `${data.compliance.delta.toFixed(1)} puntos`}</p>
      </section>
      <Entries title="Incidencias cerradas" items={data.incidents_closed} />
      <section className="soem-card p-4"><h3 className="font-bold">Evidencia de la corrección (final y de acciones)</h3>
        <EvidenceList items={data.final_evidence} /></section>
    </>}
    {data.recommendations && <Entries title="Recomendaciones" items={data.recommendations} />}
    <Records event={{ ...event, permissions: dossier.permissions }} user={user} stage={stage} onChanged={onChanged} />
  </div>;
}
